/**
 * Landing Page Builder V2 - Zustand Store
 * State management with persistence support for autosave and undo/redo
 */

import { create } from "zustand";
import {
  COMPONENT_META,
  DEFAULT_DIVIDER_HEIGHT_PX,
  DEFAULT_FOOTER_ALIGNMENT,
  DEFAULT_HERO_HEIGHT_VARIANT,
  DEFAULT_NAV_LABELS,
  MAX_UNDO_HISTORY,
  UNDO_BATCH_DELAY,
} from "../_logic/constants";
import { buildDuplicatedComponent } from "../_logic/duplicate-helpers";
import { footerAnchoredEndIndex } from "../_logic/section-bounds";
import { buildAnchorRemap, makeNavLabelUnique, remapAnchorLinks } from "../_logic/utils";
import { createDefaultNavbar } from "../_logic/utils/component-defaults";
import { type ComponentType, type PageComponent } from "../_types";
import type {
  ScheduleItemRow,
  ScheduleConfigRow,
  ScheduleLabelRow,
  ActivityRow,
  ActivityLabelRow,
  ActivityTimeSlotRow,
  SignupCounts,
} from "../_logic/types/db-types";
import { listScheduleItems, getScheduleConfig, listScheduleLabels } from "../_logic/actions/schedule-actions";
import { listActivities, listActivityLabels } from "../_logic/actions/activity-actions";
import { listTimeSlotsForEvent } from "../_logic/actions/activity-slot-actions";
import { getSignupCountsForEvent } from "../_logic/actions/activity-registration-actions";
import { setSubpageInheritance } from "../_logic/actions/page-actions";
import { deleteComponentFile } from "../_logic/storage";
import { aiTouchedComponentIds, logManualAction } from "../_logic/log-manual-action";

/**
 * Distributes `Partial` over each member of the `PageComponent` union
 * so that TypeScript checks updates against individual component types
 * rather than collapsing the union into one bag of optional properties.
 */
export type ComponentUpdate = PageComponent extends infer T
  ? T extends PageComponent
    ? Partial<Omit<T, "type" | "id">>
    : never
  : never;

/**
 * Factory function to create a new component with proper type narrowing
 * Returns a type-safe PageComponent based on the component type
 */
/** Optional event context used to prefill a new component (currently just the countdown target). */
interface NewComponentContext {
  /** Event's earliest start date (YYYY-MM-DD), used to prefill the countdown target date. */
  eventStartDate?: string;
  /** Event's start time (HH:mm) for that date, used to prefill the countdown target time. */
  eventStartTime?: string;
  /** Page-wide IANA timezone, used to prefill the countdown timezone. */
  timeZone?: string | null;
}

function createComponent(type: ComponentType, ctx?: NewComponentContext): PageComponent {
  const id = crypto.randomUUID();
  const title = COMPONENT_META[type].label;
  const navigationLabel = DEFAULT_NAV_LABELS[type];

  switch (type) {
    case "navbar":
      return { id, title, type: "navbar", visible: true, ctaText: "Register", ctaLink: "" };
    case "hero":
      return { id, title, navigationLabel, type: "hero", heightVariant: DEFAULT_HERO_HEIGHT_VARIANT };
    case "countdown":
      // Prefill the target from the event's start date so a freshly-added countdown is live
      // immediately. All three stay editable; time/zone are only meaningful alongside a date.
      return {
        id,
        title,
        navigationLabel,
        type: "countdown",
        targetDate: ctx?.eventStartDate,
        targetTime: ctx?.eventStartDate ? ctx.eventStartTime : undefined,
        timeZone: ctx?.eventStartDate ? (ctx.timeZone ?? undefined) : undefined,
      };
    case "faq":
      return { id, title, navigationLabel, type: "faq" };
    case "venue":
      return { id, title, navigationLabel, type: "venue", showButton: false };
    case "richText":
      return { id, title, navigationLabel, type: "richText" };
    case "textButton":
      return { id, title, navigationLabel, type: "textButton", showButton: true };
    case "carousel":
      return { id, title, navigationLabel, type: "carousel" };
    case "video":
      return { id, title, navigationLabel, type: "video" };
    case "schedule":
      return { id, title, navigationLabel, type: "schedule" };
    case "activities":
      return { id, title, navigationLabel, type: "activities" };
    case "team":
      return { id, title, navigationLabel, type: "team" };
    case "pricing":
      return { id, title, navigationLabel, type: "pricing" };
    case "cardGrid":
      return { id, title, navigationLabel, type: "cardGrid", items: [] };
    case "sponsors":
      return { id, title, navigationLabel, type: "sponsors", sponsors: [] };
    case "divider":
      return { id, title, navigationLabel, type: "divider", hiddenFromNav: true, height: DEFAULT_DIVIDER_HEIGHT_PX };
    case "footer":
      return {
        id,
        title,
        navigationLabel,
        type: "footer",
        hiddenFromNav: true,
        colorMode: "primary",
        alignment: DEFAULT_FOOTER_ALIGNMENT,
        showSectionsColumn: true,
        sectionsColumnHeading: "Explore",
        showSubpagesColumn: false,
        subpagesColumnHeading: "Pages",
        columns: [],
        socialLinks: [],
      };
  }
}

/** Result of {@link insertComponentIntoArray}: the updated array plus the component that was created. */
export interface InsertComponentResult {
  components: PageComponent[];
  inserted: PageComponent;
}

/**
 * Pure insert-into-array logic: creates a new component of `type` and places it into `components`,
 * honoring the hero-after-navbar and footer-anchored-at-end invariants and de-duplicating its nav
 * label. Extracted so both the single-shot `insertComponent` store action and the copilot's
 * batched multi-tool-call turn commit (ENG-147 PR5 — `commitCopilotTurn`) share one implementation
 * instead of two copies of the pinning rules drifting apart.
 * @param components - The current component array (not mutated).
 * @param type - The component type to create.
 * @param atIndex - Requested insertion index; ignored for hero (always after navbar) and clamped
 *   to before an existing footer for every other non-footer type.
 * @param ctx - Optional context used to prefill certain component types (e.g. countdown dates).
 * @returns The new array and the created component.
 */
export function insertComponentIntoArray(
  components: PageComponent[],
  type: ComponentType,
  atIndex: number,
  ctx?: NewComponentContext
): InsertComponentResult {
  const newComponent = createComponent(type, ctx);

  // Auto-suffix navigation label if it would create a duplicate anchor
  if (newComponent.navigationLabel) {
    newComponent.navigationLabel = makeNavLabelUnique(newComponent.navigationLabel, components);
  }

  // Hero always goes after navbar regardless of requested index
  if (type === "hero") {
    return {
      components: [
        ...components.filter((c) => c.type === "navbar"),
        newComponent,
        ...components.filter((c) => c.type !== "navbar"),
      ],
      inserted: newComponent,
    };
  }

  // Footer is anchored to the end; clamp non-footer insertions to before an existing footer
  const footerIndex = components.findIndex((c) => c.type === "footer");
  if (type === "footer") {
    return { components: [...components, newComponent], inserted: newComponent };
  }
  const clampedIndex = footerIndex === -1 ? atIndex : Math.min(atIndex, footerIndex);
  const next = [...components];
  next.splice(clampedIndex, 0, newComponent);
  return { components: next, inserted: newComponent };
}

/** Save status for UI indicator */
export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface BuilderState {
  /** Page ID from database (null if not persisted yet) */
  pageId: number | null;
  /** Component array (includes navbar as a component) */
  components: PageComponent[];
  /** Currently selected component ID (or navbar component ID) */
  selectedId: string | null;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Current save status for UI feedback */
  saveStatus: SaveStatus;
  /** Error message if save failed */
  saveError: string | null;
  /** Undo history stack (capped at MAX_UNDO_HISTORY) */
  undoStack: PageComponent[][];
  /** Redo history stack */
  redoStack: PageComponent[][];
  /** Pending snapshot held during debounce window for continuous edits */
  pendingSnapshot: PageComponent[] | null;
  /** Timer ID for debounce batching */
  batchTimeoutId: ReturnType<typeof setTimeout> | null;
  /** Monotonic counter incremented on reorder to trigger canvas scroll */
  scrollRevision: number;
  /** ISO timestamp of the last successful save */
  lastSavedAt: string | null;
  /** Schedule section data */
  scheduleItems: ScheduleItemRow[];
  scheduleConfig: ScheduleConfigRow | null;
  scheduleLabels: ScheduleLabelRow[];
  /** Load status for schedule data: idle → loading → loaded */
  scheduleStatus: "idle" | "loading" | "loaded";

  /** Activities section data */
  activities: ActivityRow[];
  /** Activity labels for label badges */
  activityLabels: ActivityLabelRow[];
  /** Time slots grouped by activity ID (used to render date/time on canvas cards) */
  activityTimeSlotsByActivity: Map<number, ActivityTimeSlotRow[]>;
  /** Signup counts keyed by activity ID */
  signupCounts: Record<number, SignupCounts>;
  /** Load status for activities data: idle → loading → loaded */
  activitiesStatus: "idle" | "loading" | "loaded";

  /** Whether the template modal is open */
  templateModalOpen: boolean;
  /** Whether the add-section modal is open */
  addSectionModalOpen: boolean;
  /** Optional insert index for add-section modal */
  addSectionInsertIndex: number | null;

  /** Field ID to focus in the editor (set by canvas click, consumed by editors) */
  focusedFieldId: string | null;
  /** Currently inline-editing field on the canvas (null when not editing) */
  inlineEditingField: { componentId: string; fieldId: string } | null;

  /** Optimistic locking version from the database */
  version: number;
  /** Expected version after an in-flight save (used to ignore own Realtime events) */
  pendingSaveVersion: number | null;
  /** Version from our last successful save (used to filter delayed own Realtime events) */
  lastSavedVersion: number | null;
  /** Whether a remote update is available (another user saved) */
  remoteUpdateAvailable: boolean;
  /** Remote layout_tree and version received via Realtime, pending user action */
  remoteData: { layout_tree: PageComponent[]; version: number } | null;
  /** Flag set by blur handlers to request an immediate save */
  saveRequested: boolean;
  /** URLs queued for storage deletion — flushed on successful save, cancelled on undo */
  pendingDeletions: string[];

  /**
   * Subpage inheritance — when editing a subpage, the renderer/canvas can swap
   * the subpage's hero or footer for the parent's, and merge the parent's
   * navbar styling onto the subpage's navbar. These fields are null/false when
   * editing the main page.
   */
  parentLayoutTree: PageComponent[] | null;
  inheritHeroFromParent: boolean;
  inheritFooterFromParent: boolean;
  inheritNavbarFromParent: boolean;
}

/** Subpage inheritance context passed into `initialize()` when editing a subpage. */
export interface InheritanceInit {
  parentLayoutTree: PageComponent[] | null;
  inheritHeroFromParent: boolean;
  inheritFooterFromParent: boolean;
  inheritNavbarFromParent: boolean;
}

interface BuilderActions {
  /** Initialize store with data from database. Version defaults to 0 if not provided. */
  initialize: (pageId: number, components: PageComponent[], version?: number, inheritance?: InheritanceInit) => void;
  /**
   * Toggle whether the current subpage inherits the parent's hero. Optimistic;
   * reverts if the server action fails. No-op when editing the main page.
   */
  setInheritHeroFromParent: (value: boolean) => Promise<void>;
  /**
   * Toggle whether the current subpage inherits the parent's footer. Optimistic;
   * reverts if the server action fails. No-op when editing the main page.
   */
  setInheritFooterFromParent: (value: boolean) => Promise<void>;
  /**
   * Toggle whether the current subpage inherits the parent navbar's styling
   * (logo, icon, CTA, colors). Nav links stay generated from this subpage's
   * own sections. Optimistic; reverts if the server action fails. No-op when
   * editing the main page.
   */
  setInheritNavbarFromParent: (value: boolean) => Promise<void>;
  /** Reset store to initial state */
  reset: () => void;
  /** Set selected component ID */
  setSelectedId: (id: string | null) => void;
  /** Reorder components (excludes navbar - only sections) */
  reorderComponents: (fromIndex: number, toIndex: number) => void;
  /** Add a new component (appends to end) */
  addComponent: (type: ComponentType, ctx?: NewComponentContext) => void;
  /** Insert a new component at a specific index */
  insertComponent: (type: ComponentType, atIndex: number, ctx?: NewComponentContext) => void;
  /** Delete a component */
  deleteComponent: (id: string) => void;
  /** Duplicate a component (deep clone with new IDs) and insert after the original */
  duplicateComponent: (id: string) => void;
  /** Update a component's properties (type-safe per component variant). `source: "ai"` marks an
   *  update applied directly by a copilot tool (e.g. `applySuggestImage`) that already logs its
   *  own audit-log row — the manual-edit diff logging in `commitBatch` skips these ids so the
   *  same edit isn't double-logged, once correctly and once misattributed as manual. */
  updateComponent: (id: string, updates: ComponentUpdate, source?: "ai") => void;
  /**
   * Reverts a single component to a copilot action's `before_state` snapshot (ENG-147).
   * `snapshot: null` removes the component (reverting an insert); otherwise the component at
   * `componentId` is replaced with `snapshot` if present, or re-inserted at the end if it was
   * since deleted. Always a single-component operation — never touches the rest of the page.
   */
  revertToActionSnapshot: (componentId: string, snapshot: PageComponent | null) => void;
  /**
   * Commits a whole copilot turn's worth of changes (ENG-147 PR5) in one state transition: one
   * undo snapshot and one re-render, instead of one of each per tool call in a compound request.
   * `finalComponents` is the fully-folded result — callers apply each queued change sequentially
   * in memory first (see `useCopilotTools`'s `flushTurn`), then pass the final array here.
   */
  commitCopilotTurn: (finalComponents: PageComponent[]) => void;
  /** Get current components (for saving) */
  getComponents: () => PageComponent[];
  /** Mark as saved (called after successful autosave), optionally updating the version */
  markAsSaved: (newVersion?: number) => void;
  /** Mark save as started */
  markSaveStarted: () => void;
  /** Mark save as failed */
  markSaveFailed: (error: string) => void;
  /** Push an immediate snapshot onto the undo stack (for discrete actions) */
  pushSnapshot: () => void;
  /** Start or extend a debounced snapshot (for continuous input like typing) */
  debouncedSnapshot: () => void;
  /** Flush the pending debounced snapshot into the undo stack */
  commitBatch: () => void;
  /** Undo the last change */
  undo: () => void;
  /** Redo the last undone change */
  redo: () => void;
  /** Fetch schedule data (items, config, labels) from DB into store */
  loadScheduleData: (eventId: number, landingPageId: number) => Promise<void>;
  /** Replace all schedule items (for reorder/sort) */
  setScheduleItems: (items: ScheduleItemRow[]) => void;
  /** Add a single schedule item (after create/duplicate) */
  addScheduleItem: (item: ScheduleItemRow) => void;
  /** Update a single schedule item in place */
  updateScheduleItemInStore: (item: ScheduleItemRow) => void;
  /** Remove a schedule item by ID */
  removeScheduleItem: (id: number) => void;
  /** Update schedule config */
  setScheduleConfig: (config: ScheduleConfigRow) => void;
  /** Update schedule labels */
  setScheduleLabels: (labels: ScheduleLabelRow[]) => void;

  /** Fetch activities data (activities, signup counts) from DB into store */
  loadActivitiesData: (eventId: number) => Promise<void>;
  /** Replace all activities (for reorder) */
  setActivities: (activities: ActivityRow[]) => void;
  /** Add a single activity (after create/duplicate) */
  addActivity: (activity: ActivityRow) => void;
  /** Update a single activity in place */
  updateActivityInStore: (activity: ActivityRow) => void;
  /** Remove an activity by ID */
  removeActivity: (id: number) => void;
  /** Replace activity labels */
  setActivityLabels: (labels: ActivityLabelRow[]) => void;
  /** Reset schedule and activities load status to idle (triggers re-fetch) */
  resetDataStatus: () => void;

  /** Replace all components with AI-generated template, preserving undo history */
  applyTemplate: (components: PageComponent[]) => void;

  /** Open the template modal */
  openTemplateModal: () => void;
  /** Close the template modal */
  closeTemplateModal: () => void;
  /** Open the add-section modal, optionally at a specific insert index */
  openAddSectionModal: (insertAtIndex?: number) => void;
  /** Close the add-section modal */
  closeAddSectionModal: () => void;
  /** Set the focused field ID (for canvas click → editor focus) */
  setFocusedField: (fieldId: string | null) => void;
  /** Set the field currently being inline-edited on the canvas */
  setInlineEditingField: (field: { componentId: string; fieldId: string } | null) => void;

  /** Update version after a successful save */
  setVersion: (version: number) => void;
  /** Apply remote components from another user's save without marking dirty */
  syncRemoteComponents: (components: PageComponent[], version: number) => void;
  /** Flag that a remote update is available (used when local state is dirty) */
  setRemoteUpdate: (data: { layout_tree: PageComponent[]; version: number } | null) => void;
  /** Request an immediate save (called on blur from editors) */
  requestSave: () => void;
  /** Queue a storage URL for deferred deletion (flushed on save) */
  queueDeletion: (url: string) => void;
  /** Flush all pending deletions (call after successful save) */
  flushDeletions: () => void;
}

const initialState: BuilderState = {
  pageId: null,
  components: [],
  selectedId: null,
  isDirty: false,
  saveStatus: "idle",
  saveError: null,
  undoStack: [],
  redoStack: [],
  pendingSnapshot: null,
  batchTimeoutId: null,
  scrollRevision: 0,
  lastSavedAt: null,
  scheduleItems: [],
  scheduleConfig: null,
  scheduleLabels: [],
  scheduleStatus: "idle",
  activities: [],
  activityLabels: [],
  activityTimeSlotsByActivity: new Map(),
  signupCounts: {},
  activitiesStatus: "idle",
  templateModalOpen: false,
  addSectionModalOpen: false,
  addSectionInsertIndex: null,
  focusedFieldId: null,
  inlineEditingField: null,
  version: 0,
  pendingSaveVersion: null,
  lastSavedVersion: null,
  remoteUpdateAvailable: false,
  remoteData: null,
  saveRequested: false,
  pendingDeletions: [],
  parentLayoutTree: null,
  inheritHeroFromParent: false,
  inheritFooterFromParent: false,
  inheritNavbarFromParent: false,
};

/**
 * Zustand store for landing page builder state management
 * Manages component list, selection state, persistence, and undo/redo history
 */
export const useBuilderStore = create<BuilderState & BuilderActions>((set, get) => ({
  // Initial state
  ...initialState,

  // Actions
  initialize: (pageId, components, version, inheritance) => {
    // Ensure navbar component exists (backward compat with old layout_trees)
    let finalComponents = components;
    if (!components.find((c) => c.type === "navbar")) {
      finalComponents = [createDefaultNavbar(), ...components];
    }

    // Clear any pending batch timer
    const {
      batchTimeoutId,
      selectedId: currentSelectedId,
      pageId: currentPageId,
      version: currentVersion,
      pendingSaveVersion,
      lastSavedVersion,
      components: currentComponents,
    } = get();
    if (batchTimeoutId) clearTimeout(batchTimeoutId);

    const resolvedVersion = version ?? 0;

    // Server actions like createSubpage() can revalidate the current route and cause
    // BuilderLayout to call initialize() again with stale server-rendered components — same
    // self-echo problem usePageSync's Realtime handler guards against, so this reuses its
    // three-way staleness check (version, pendingSaveVersion, lastSavedVersion) rather than
    // comparing only the stored version, which missed a fetch that resolves after an in-flight
    // save has already moved `version` ahead.
    const isStaleOrCurrent =
      resolvedVersion <= currentVersion ||
      (pendingSaveVersion !== null && resolvedVersion <= pendingSaveVersion) ||
      (lastSavedVersion !== null && resolvedVersion <= lastSavedVersion);
    if (currentPageId === pageId && isStaleOrCurrent && currentComponents.length > 0) {
      set({
        parentLayoutTree: inheritance?.parentLayoutTree ?? null,
        inheritHeroFromParent: inheritance?.inheritHeroFromParent ?? false,
        inheritFooterFromParent: inheritance?.inheritFooterFromParent ?? false,
        inheritNavbarFromParent: inheritance?.inheritNavbarFromParent ?? false,
      });
      return;
    }

    // Preserve current selection if the component still exists (e.g., after revalidatePath re-init)
    const preserveSelection = currentSelectedId && finalComponents.some((c) => c.id === currentSelectedId);

    set({
      pageId,
      components: finalComponents,
      selectedId: preserveSelection
        ? currentSelectedId
        : finalComponents.find((c) => c.type === "hero")?.id ||
          finalComponents.find((c) => c.type !== "navbar")?.id ||
          null,
      isDirty: false,
      saveStatus: "idle",
      saveError: null,
      undoStack: [],
      redoStack: [],
      pendingSnapshot: null,
      batchTimeoutId: null,
      scrollRevision: 0,
      lastSavedAt: null,
      version: resolvedVersion,
      pendingSaveVersion: null,
      lastSavedVersion: null,
      remoteUpdateAvailable: false,
      remoteData: null,
      saveRequested: false,
      parentLayoutTree: inheritance?.parentLayoutTree ?? null,
      inheritHeroFromParent: inheritance?.inheritHeroFromParent ?? false,
      inheritFooterFromParent: inheritance?.inheritFooterFromParent ?? false,
      inheritNavbarFromParent: inheritance?.inheritNavbarFromParent ?? false,
    });
  },

  setInheritHeroFromParent: async (value) => {
    const { pageId, parentLayoutTree, inheritHeroFromParent } = get();
    if (pageId === null || parentLayoutTree === null) return;
    if (inheritHeroFromParent === value) return;

    set({ inheritHeroFromParent: value });
    const result = await setSubpageInheritance(pageId, { inheritHeroFromParent: value });
    if (!result.success) {
      set({ inheritHeroFromParent: !value, saveError: result.error ?? "Failed to update inheritance" });
    }
  },

  setInheritFooterFromParent: async (value) => {
    const { pageId, parentLayoutTree, inheritFooterFromParent } = get();
    if (pageId === null || parentLayoutTree === null) return;
    if (inheritFooterFromParent === value) return;

    set({ inheritFooterFromParent: value });
    const result = await setSubpageInheritance(pageId, { inheritFooterFromParent: value });
    if (!result.success) {
      set({ inheritFooterFromParent: !value, saveError: result.error ?? "Failed to update inheritance" });
    }
  },

  setInheritNavbarFromParent: async (value) => {
    const { pageId, parentLayoutTree, inheritNavbarFromParent } = get();
    if (pageId === null || parentLayoutTree === null) return;
    if (inheritNavbarFromParent === value) return;

    set({ inheritNavbarFromParent: value });
    const result = await setSubpageInheritance(pageId, { inheritNavbarFromParent: value });
    if (!result.success) {
      set({ inheritNavbarFromParent: !value, saveError: result.error ?? "Failed to update inheritance" });
    }
  },

  reset: () => {
    const { batchTimeoutId } = get();
    if (batchTimeoutId) clearTimeout(batchTimeoutId);
    set(initialState);
  },

  setSelectedId: (id) => set({ selectedId: id }),

  reorderComponents: (fromIndex, toIndex) => {
    const current = get().components;
    if (fromIndex < 0 || fromIndex >= current.length) return;

    // Footer is anchored to the end: it cannot move, and nothing may move to or past its position
    const footerIndex = current.findIndex((c) => c.type === "footer");
    if (footerIndex !== -1) {
      if (fromIndex === footerIndex) return;
      if (toIndex >= footerIndex) return;
    }

    get().pushSnapshot();
    const newComponents = [...current];
    const [removed] = newComponents.splice(fromIndex, 1);
    newComponents.splice(toIndex, 0, removed);
    set((state) => ({
      components: newComponents,
      selectedId: removed.id,
      isDirty: true,
      saveRequested: true,
      saveStatus: "idle",
      scrollRevision: state.scrollRevision + 1,
    }));
    // Not component-scoped (a reorder touches two positions) — matches the AI's own
    // `reorderComponents` tool logging, which is also `isRevertible: false` for the same reason.
    logManualAction(() => get().pageId, {
      actionType: "reorder_components",
      componentId: null,
      description: `Reordered "${removed.title}"`,
      beforeState: null,
      afterState: null,
      isRevertible: false,
    });
  },

  addComponent: (type, ctx) => {
    get().pushSnapshot();
    const currentComponents = get().components;
    const newComponent = createComponent(type, ctx);

    // Auto-suffix navigation label if it would create a duplicate anchor
    if (newComponent.navigationLabel) {
      newComponent.navigationLabel = makeNavLabelUnique(newComponent.navigationLabel, currentComponents);
    }

    // Hero always goes after navbar; footer is anchored to the end (non-footer additions go
    // before an existing one).
    let newComponents: PageComponent[];
    if (type === "hero") {
      newComponents = [
        ...currentComponents.filter((c) => c.type === "navbar"),
        newComponent,
        ...currentComponents.filter((c) => c.type !== "navbar"),
      ];
    } else {
      const footerIndex = currentComponents.findIndex((c) => c.type === "footer");
      newComponents = [...currentComponents];
      if (type === "footer" || footerIndex === -1) {
        newComponents.push(newComponent);
      } else {
        newComponents.splice(footerIndex, 0, newComponent);
      }
    }

    set({
      components: newComponents,
      selectedId: newComponent.id,
      isDirty: true,
      saveRequested: true,
      saveStatus: "idle",
    });
    logManualAction(() => get().pageId, {
      actionType: "insert_component",
      componentId: newComponent.id,
      description: `Added a ${COMPONENT_META[type].label} section`,
      beforeState: null,
      afterState: newComponent,
      isRevertible: true,
    });
  },

  insertComponent: (type, atIndex, ctx) => {
    get().pushSnapshot();
    const { components: newComponents, inserted } = insertComponentIntoArray(get().components, type, atIndex, ctx);
    set({
      components: newComponents,
      selectedId: inserted.id,
      isDirty: true,
      saveRequested: true,
      saveStatus: "idle",
    });
    logManualAction(() => get().pageId, {
      actionType: "insert_component",
      componentId: inserted.id,
      description: `Added a ${COMPONENT_META[type].label} section`,
      beforeState: null,
      afterState: inserted,
      isRevertible: true,
    });
  },

  deleteComponent: (id) => {
    const target = get().components.find((c) => c.id === id);
    get().pushSnapshot();
    set((state) => {
      const idx = state.components.findIndex((c) => c.id === id);
      const newComponents = state.components.filter((c) => c.id !== id);
      let newSelectedId = state.selectedId;
      if (state.selectedId === id) {
        // Select adjacent section: prefer the one below, then above
        const adjacent = newComponents[idx] ?? newComponents[idx - 1];
        newSelectedId =
          (adjacent?.type !== "navbar" ? adjacent?.id : newComponents.find((c) => c.type !== "navbar")?.id) ?? null;
      }
      return {
        components: newComponents,
        selectedId: newSelectedId,
        isDirty: true,
        saveRequested: true,
        saveStatus: "idle",
      };
    });
    if (target) {
      logManualAction(() => get().pageId, {
        actionType: "delete_component",
        componentId: id,
        description: `Deleted the "${target.title}" section`,
        beforeState: target,
        afterState: null,
        isRevertible: true,
      });
    }
  },

  duplicateComponent: (id) => {
    const state = get();
    const sourceIndex = state.components.findIndex((c) => c.id === id);
    if (sourceIndex === -1) return;

    get().pushSnapshot();

    const source = state.components[sourceIndex];
    const clone = buildDuplicatedComponent(source, state.components);

    const next = [...state.components];
    next.splice(sourceIndex + 1, 0, clone);
    set({ components: next, selectedId: clone.id, isDirty: true, saveRequested: true, saveStatus: "idle" });
    logManualAction(() => get().pageId, {
      actionType: "duplicate_component",
      componentId: clone.id,
      description: `Duplicated "${source.title}"`,
      beforeState: null,
      afterState: clone,
      isRevertible: true,
    });
  },

  updateComponent: (id, updates, source) => {
    if (source === "ai") aiTouchedComponentIds.add(id);
    get().debouncedSnapshot();
    set((state) => {
      const components = state.components.map((c) => {
        if (c.id !== id) return c;
        // Preserve the original `type` discriminant so the union stays valid
        return { ...c, ...updates, type: c.type } as PageComponent;
      });

      // Renaming a section changes its derived #anchor id, so every CTA still pointing at the
      // old label would silently scroll nowhere. Rewrite those links in lockstep with the
      // rename (including any anchors that renumbered as a side effect) so they keep resolving.
      const nextComponents =
        "navigationLabel" in updates
          ? remapAnchorLinks(components, buildAnchorRemap(state.components, components))
          : components;

      return { components: nextComponents, isDirty: true, saveStatus: "idle" };
    });
  },

  revertToActionSnapshot: (componentId, snapshot) => {
    get().pushSnapshot();
    set((state) => {
      const exists = state.components.some((c) => c.id === componentId);

      // Reverting an insert: the snapshot is null, so the component is removed.
      if (!snapshot) {
        return {
          components: state.components.filter((c) => c.id !== componentId),
          selectedId: state.selectedId === componentId ? null : state.selectedId,
          isDirty: true,
          saveRequested: true,
          saveStatus: "idle",
        };
      }

      // Reverting an update: replace in place. Reverting a delete: the component no longer
      // exists in the array, so append the snapshot back (footer stays anchored last).
      if (exists) {
        return {
          components: state.components.map((c) => (c.id === componentId ? snapshot : c)),
          isDirty: true,
          saveRequested: true,
          saveStatus: "idle",
        };
      }

      // Footer-anchoring rule shared with `insertComponentIntoArray`/`applyQueuedChange` via
      // `footerAnchoredEndIndex` — a footer snapshot always re-appends at the very end; anything
      // else goes back in right before an existing footer (or the end, if there isn't one).
      const restored = [...state.components];
      if (snapshot.type === "footer") {
        restored.push(snapshot);
      } else {
        restored.splice(footerAnchoredEndIndex(state.components), 0, snapshot);
      }

      return {
        components: restored,
        selectedId: snapshot.id,
        isDirty: true,
        saveRequested: true,
        saveStatus: "idle",
      };
    });
  },

  commitCopilotTurn: (finalComponents) => {
    get().pushSnapshot();
    // A copilot turn can rename sections (navigationLabel is a writable field), which changes
    // their derived #anchor ids. Rewrite CTA links in lockstep — same as the manual rename path
    // in updateComponent — so an AI edit doesn't silently orphan links pointing at the old label.
    // Limitation: the remap keys on old anchor ids, so if one turn both renames a section off
    // #x AND adds a new section that reclaims #x, a link the AI freshly set to #x is rewritten
    // toward the renamed section. Rare, and a symptom of string-based anchor refs — the proper
    // fix is the stable-reference resolution tracked as a follow-up.
    const components = remapAnchorLinks(finalComponents, buildAnchorRemap(get().components, finalComponents));
    set({
      components,
      isDirty: true,
      saveRequested: true,
      saveStatus: "idle",
    });
  },

  getComponents: () => get().components,

  markAsSaved: (newVersion) =>
    set((state) => {
      // If saveStatus is no longer "saving", edits happened during the in-flight save.
      // Preserve isDirty/saveRequested so those edits trigger another save.
      const editedDuringSave = state.saveStatus !== "saving";
      const resolvedVersion = newVersion ?? state.version;
      return {
        isDirty: editedDuringSave ? state.isDirty : false,
        saveRequested: editedDuringSave ? state.saveRequested : false,
        saveStatus: "saved",
        saveError: null,
        lastSavedAt: new Date().toISOString(),
        version: resolvedVersion,
        pendingSaveVersion: null,
        lastSavedVersion: resolvedVersion,
      };
    }),

  markSaveStarted: () =>
    set((state) => ({
      saveStatus: "saving",
      saveError: null,
      pendingSaveVersion: state.version + 1,
    })),

  markSaveFailed: (error) =>
    set((state) => {
      const editedDuringSave = state.saveStatus !== "saving";
      return {
        saveStatus: "error",
        saveError: error,
        pendingSaveVersion: null,
        saveRequested: editedDuringSave ? state.saveRequested : false,
      };
    }),

  // Undo/Redo actions

  pushSnapshot: () =>
    set((state) => ({
      undoStack: [...state.undoStack, state.components].slice(-MAX_UNDO_HISTORY),
      redoStack: [],
    })),

  debouncedSnapshot: () => {
    const { pendingSnapshot, batchTimeoutId, components, commitBatch } = get();

    // Capture the first snapshot in this batch window
    if (!pendingSnapshot) {
      set({ pendingSnapshot: components });
    }

    // Reset the timer
    if (batchTimeoutId) clearTimeout(batchTimeoutId);

    const newTimeoutId = setTimeout(() => commitBatch(), UNDO_BATCH_DELAY);
    set({ batchTimeoutId: newTimeoutId });
  },

  commitBatch: () => {
    const { pendingSnapshot, batchTimeoutId, components: afterComponents, pageId } = get();
    if (!pendingSnapshot) return;

    if (batchTimeoutId) clearTimeout(batchTimeoutId);

    // Diff the snapshot taken when this batch started against the current state, and log one
    // manual-edit action per component whose content actually changed — insert/delete/reorder/
    // duplicate already log immediately at their own call sites, so this only needs to catch
    // in-place field edits (typing, image/list reordering, etc.) batched by `debouncedSnapshot`.
    // Skips any id the AI already logged itself (`updateComponent(id, updates, "ai")`).
    if (pageId) {
      const beforeById = new Map(pendingSnapshot.map((c) => [c.id, c]));
      for (const after of afterComponents) {
        if (aiTouchedComponentIds.has(after.id)) continue;
        const before = beforeById.get(after.id);
        if (before && JSON.stringify(before) !== JSON.stringify(after)) {
          logManualAction(() => get().pageId, {
            actionType: "update_component",
            componentId: after.id,
            description: `Updated the "${after.title}" section`,
            beforeState: before,
            afterState: after,
            isRevertible: true,
          });
        }
      }
    }
    aiTouchedComponentIds.clear();

    set((state) => ({
      undoStack: [...state.undoStack, pendingSnapshot].slice(-MAX_UNDO_HISTORY),
      redoStack: [],
      pendingSnapshot: null,
      batchTimeoutId: null,
    }));
  },

  undo: () => {
    // Flush any pending debounced edits first
    get().commitBatch();

    set((state) => {
      if (state.undoStack.length === 0) return state;

      const restoredComponents = state.undoStack[state.undoStack.length - 1];

      // Cancel pending deletions for image URLs that are back in the restored state
      let pendingDeletions = state.pendingDeletions;
      if (pendingDeletions.length > 0) {
        const restoredUrls = new Set(
          restoredComponents.flatMap((c) =>
            "images" in c && Array.isArray(c.images) ? c.images.map((img: { url: string }) => img.url) : []
          )
        );
        pendingDeletions = pendingDeletions.filter((url) => !restoredUrls.has(url));
      }

      return {
        components: restoredComponents,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.components],
        pendingDeletions,
        isDirty: true,
        saveRequested: true,
        saveStatus: "idle",
      };
    });
  },

  redo: () => {
    // Flush any pending debounced edits first
    get().commitBatch();

    set((state) => {
      if (state.redoStack.length === 0) return state;

      const redoTarget = state.redoStack[state.redoStack.length - 1];

      // Re-queue deletions for image URLs that disappear in the redo transition
      let pendingDeletions = state.pendingDeletions;
      const currentUrls = new Set(
        state.components.flatMap((c) =>
          "images" in c && Array.isArray(c.images) ? c.images.map((img: { url: string }) => img.url) : []
        )
      );
      const redoUrls = new Set(
        redoTarget.flatMap((c) =>
          "images" in c && Array.isArray(c.images) ? c.images.map((img: { url: string }) => img.url) : []
        )
      );
      const removedUrls = [...currentUrls].filter((url) => !redoUrls.has(url));
      if (removedUrls.length > 0) {
        pendingDeletions = [...pendingDeletions, ...removedUrls];
      }

      return {
        components: redoTarget,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, state.components],
        pendingDeletions,
        isDirty: true,
        saveRequested: true,
        saveStatus: "idle",
      };
    });
  },
  // Schedule slice actions
  loadScheduleData: async (eventId, landingPageId) => {
    if (get().scheduleStatus === "loading") return;
    set({ scheduleStatus: "loading" });
    try {
      const [items, config, labels] = await Promise.all([
        listScheduleItems(eventId),
        getScheduleConfig(landingPageId),
        listScheduleLabels(eventId),
      ]);
      set({ scheduleItems: items, scheduleConfig: config, scheduleLabels: labels, scheduleStatus: "loaded" });
    } catch (error) {
      console.error("loadScheduleData: failed to fetch", error);
      set({ scheduleStatus: "idle" });
    }
  },

  setScheduleItems: (items) => set({ scheduleItems: items }),

  addScheduleItem: (item) => set((state) => ({ scheduleItems: [...state.scheduleItems, item] })),

  updateScheduleItemInStore: (item) =>
    set((state) => ({
      scheduleItems: state.scheduleItems.map((i) => (i.id === item.id ? item : i)),
    })),

  removeScheduleItem: (id) =>
    set((state) => ({
      scheduleItems: state.scheduleItems.filter((i) => i.id !== id),
    })),

  setScheduleConfig: (config) => set({ scheduleConfig: config }),

  setScheduleLabels: (labels) => set({ scheduleLabels: labels }),

  // Activities slice actions
  loadActivitiesData: async (eventId) => {
    if (get().activitiesStatus === "loading") return;
    set({ activitiesStatus: "loading" });
    try {
      const [activitiesData, labelsData, countsData, slotsData] = await Promise.all([
        listActivities(eventId),
        listActivityLabels(eventId),
        getSignupCountsForEvent(eventId),
        listTimeSlotsForEvent(eventId),
      ]);
      const countsMap: Record<number, SignupCounts> = {};
      for (const c of countsData) {
        countsMap[c.activity_id] = c;
      }
      const slotMap = new Map<number, ActivityTimeSlotRow[]>();
      for (const slot of slotsData) {
        const existing = slotMap.get(slot.activity_id) ?? [];
        existing.push(slot);
        slotMap.set(slot.activity_id, existing);
      }
      set({
        activities: activitiesData,
        activityLabels: labelsData,
        signupCounts: countsMap,
        activityTimeSlotsByActivity: slotMap,
        activitiesStatus: "loaded",
      });
    } catch (error) {
      console.error("loadActivitiesData: failed to fetch", error);
      set({ activitiesStatus: "idle" });
    }
  },

  setActivities: (activities) => set({ activities }),

  addActivity: (activity) => set((state) => ({ activities: [...state.activities, activity] })),

  updateActivityInStore: (activity) =>
    set((state) => ({
      activities: state.activities.map((a) => (a.id === activity.id ? activity : a)),
    })),

  removeActivity: (id) =>
    set((state) => ({
      activities: state.activities.filter((a) => a.id !== id),
    })),

  setActivityLabels: (labels) => set({ activityLabels: labels }),

  resetDataStatus: () => set({ scheduleStatus: "idle", activitiesStatus: "idle" }),

  // Template actions
  applyTemplate: (components) => {
    get().commitBatch();
    set((state) => ({
      components,
      undoStack: [...state.undoStack, state.components].slice(-MAX_UNDO_HISTORY),
      redoStack: [],
      pendingSnapshot: null,
      selectedId:
        components.find((c) => c.type === "hero")?.id || components.find((c) => c.type !== "navbar")?.id || null,
      isDirty: true,
      saveRequested: true,
      saveStatus: "idle",
    }));
  },

  // Modal actions
  openTemplateModal: () => set({ templateModalOpen: true }),
  closeTemplateModal: () => set({ templateModalOpen: false }),
  openAddSectionModal: (insertAtIndex) =>
    set({ addSectionModalOpen: true, addSectionInsertIndex: insertAtIndex ?? null }),
  closeAddSectionModal: () => set({ addSectionModalOpen: false, addSectionInsertIndex: null }),
  setFocusedField: (fieldId) => set({ focusedFieldId: fieldId }),
  setInlineEditingField: (field) => set({ inlineEditingField: field }),

  // Version & sync actions
  setVersion: (version) => set({ version }),

  syncRemoteComponents: (components, version) => {
    // Ensure navbar component exists (backward compat)
    let finalComponents = components;
    if (!components.find((c) => c.type === "navbar")) {
      finalComponents = [createDefaultNavbar(), ...components];
    }

    const { selectedId: currentSelectedId } = get();
    const preserveSelection = currentSelectedId && finalComponents.some((c) => c.id === currentSelectedId);

    set({
      components: finalComponents,
      selectedId: preserveSelection
        ? currentSelectedId
        : finalComponents.find((c) => c.type === "hero")?.id ||
          finalComponents.find((c) => c.type !== "navbar")?.id ||
          null,
      version,
      isDirty: false,
      saveStatus: "saved",
      lastSavedAt: new Date().toISOString(),
      lastSavedVersion: null,
      remoteUpdateAvailable: false,
      remoteData: null,
    });
  },

  setRemoteUpdate: (data) =>
    set({
      remoteUpdateAvailable: !!data,
      remoteData: data,
    }),

  requestSave: () => {
    const { isDirty } = get();
    if (isDirty) {
      set({ saveRequested: true });
    }
  },

  queueDeletion: (url: string) =>
    set((state) => ({
      pendingDeletions: [...state.pendingDeletions, url],
    })),

  flushDeletions: () => {
    const { pendingDeletions } = get();
    if (pendingDeletions.length === 0) return;
    // Fire-and-forget — deletions are best-effort cleanup
    Promise.allSettled(pendingDeletions.map((url) => deleteComponentFile(url))).then((results) => {
      results.forEach((r, i) => {
        if (r.status === "rejected") console.error("flushDeletions: failed to delete", pendingDeletions[i], r.reason);
      });
    });
    set({ pendingDeletions: [] });
  },
}));
